import { createAvatar } from '@dicebear/core';
import { notionists } from '@dicebear/collection';

const cache = new Map<string, string>();

/** Soft disc behind the face (hex without # for DiceBear). */
const ROLE_PERSONA_BG: Record<string, string> = {
  SUPER_ADMIN: 'd8f3dc', // menthol-50
  CATALOG: 'e9ede8', // sage-100
  BUYER: 'f6f4ee', // cream
  WAREHOUSE: 'b7e4c7', // menthol-200
  ACCOUNTANT: 'e8efe9',
  SALES: 'f0ebe3',
  VIEWER: 'f4f1ea',
};

/** Line / fill ink on the drawing itself (WaPharma pine–brunswick–menthol). */
const ROLE_PERSONA_INK: Record<string, string> = {
  SUPER_ADMIN: '#0f2419', // pine-950
  CATALOG: '#1b4332', // brunswick-800
  BUYER: '#2d6a4f', // brunswick-600
  WAREHOUSE: '#1f6b4a', // success / deep menthol
  ACCOUNTANT: '#1b4332',
  SALES: '#2d6a4f',
  VIEWER: '#3d4f5d', // feldspar-700 — cooler read-only
};

const BRAND_BG_FALLBACK = ['f6f4ee', 'e9ede8', 'd8f3dc', 'e8efe9', 'f0ebe3'] as const;
const BRAND_INK_FALLBACK = ['#1b4332', '#2d6a4f', '#0f2419', '#1f6b4a'] as const;

function hashRole(role: string): number {
  let h = 0;
  for (let i = 0; i < role.length; i += 1) h = (h + role.charCodeAt(i) * (i + 1)) % 997;
  return h;
}

function backgroundForRole(role: string): string {
  return ROLE_PERSONA_BG[role] ?? BRAND_BG_FALLBACK[hashRole(role) % BRAND_BG_FALLBACK.length]!;
}

function inkForRole(role: string): string {
  return ROLE_PERSONA_INK[role] ?? BRAND_INK_FALLBACK[hashRole(role) % BRAND_INK_FALLBACK.length]!;
}

/** Paint notionists (#000 / #fff) with brand ink + cream body. */
function tintNotionistsSvg(svg: string, ink: string): string {
  return svg
    .replace(/#000000|#000\b/gi, ink)
    .replace(/#ffffff|#fff\b/gi, '#f6f4ee'); // cream body fills
}

/** Deterministic Notion-style face, zoomed head, WaPharma colors on the drawing. */
export function rolePersonaDataUri(role: string): string {
  const seed = role.trim() || 'role';
  const hit = cache.get(seed);
  if (hit) return hit;

  const raw = createAvatar(notionists, {
    seed,
    size: 96,
    scale: 195,
    translateY: 16,
    backgroundColor: [backgroundForRole(seed)],
  }).toString();

  const svg = tintNotionistsSvg(raw, inkForRole(seed));
  const uri = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  cache.set(seed, uri);
  return uri;
}
